// src/ui/PostVideo.jsx — a Vibes video, with the track that was added to it.
//
// ══ THE BUG THIS FIXES ═════════════════════════════════════════════════════
// The web played the raw video file and nothing else, so a reel you had scored
// with a song played its ORIGINAL camera audio instead — the one sound the
// author had usually muted on purpose. The music was never missing from the
// data; the web simply never looked for it.
//
// ══ THE MODEL (the app's, ported) ══════════════════════════════════════════
// A track is NEVER muxed into the video. The post carries a second file:
//
//     audioTrackUrl      the song
//     audioTrackTitle    what the pill says
//     isOriginalMuted    the camera audio, muted by the author (default false)
//     isAddedSoundMuted  the song, muted by the author (default true)
//     originalVolume     0–100 for the camera audio
//     addedSoundVolume   0–100 for the song
//
// 🟥 THE VIDEO IS THE MASTER CLOCK AND THE MUSIC IS SLAVED TO IT. The song gets
//    no autoplay of its own — it is driven entirely from the video's clock:
//
//      video playing and not stalled  → song plays at videoTime % songDuration
//      video paused / buffering       → song pauses in the same frame
//      drift over 300ms               → song snaps back
//
//    The app's comment records why: when the song owned its own playback, a weak
//    connection buffered the video while the music carried on, and the two never
//    met again. Modulo the song's duration so a long video keeps looping a short
//    track in the right place, exactly as the phone does it.
//
// 🟥 AND AUTOPLAY. A browser will not start AUDIBLE audio without a user
//    gesture. Reels are opened by a click, so by the time this mounts the
//    gesture has happened and play() resolves — but a muted-autoplay feed video
//    scrolling into view has NOT earned one, so the rejection is caught and the
//    video is left to play silently rather than throwing. `gestureless` says
//    "don't even try", for surfaces that autoplay without a click.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import VolumeControl, { useVolume } from './VolumeControl';
import { Play, Pause } from './icons';
/* ✂️ a trimmed clip is a stored RANGE, honoured at playback on both clients —
   see services/mediaEditor */
import { trimOf, seekForTrim } from '../services/mediaEditor';
import { REEL_FAST_RATE, REEL_HOLD_MS } from '../services/reelFollow';
/* 📶 the four-rung ladder the server builds for every clip, which this player
   has never once asked for — see services/adaptiveVideo */
import { attachAdaptive, hlsUrlFor } from '../services/adaptiveVideo';
/* ⏱ how much of it was actually watched — see services/feedSignals */
import * as feedSignals from '../services/feedSignals';
/* 🎵 where the song should be for where the video is — one arithmetic with the
   phone and the editor that chose it (services/soundLibrary) */
import { musicPositionFor, effectiveVolumes } from '../services/soundLibrary';

const vol = (v) => Math.max(0, Math.min(1, (Number.isFinite(Number(v)) ? Number(v) : 100) / 100));

/* ══ ONE VIDEO AT A TIME, EVERYWHERE ═══════════════════════════════════════
   🟥 A TALL WINDOW CAN HOLD TWO FEED VIDEOS AT ONCE, and "is it 60% visible"
      was true of both — so both played, both buffered, and the moment the
      sound was turned on they played over each other. Autoplay needs an
      arbiter, not just a threshold.

   Whichever player starts most recently wins and the previous one is paused
   (its added track with it). Deliberately module-level rather than React
   state: it is a fact about the page, not about any one component, and the
   handover has to happen in the same tick the browser starts playback. */
const PLAYERS = new Map();   // <video> → the function that stops it and its music
let current = null;
function claimPlayback(v) {
  if (current && current !== v) {
    const stop = PLAYERS.get(current);
    if (stop) { try { stop(); } catch {} }
  }
  current = v;
}

/** m:ss — the only format a clip under an hour has ever needed. */
const fmt = (s) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, '0')}`;
};

export default function PostVideo({
  post, src, poster, className, controls = false, loop = true,
  muted = false, gestureless = false, onClick, autoPlayInView = false,
  volumeScope = null, chrome = false, fitTolerance = 0,
  onWatched = null,   // 👁️ called once, when this clip has actually played 1.5 s
  holdToFast = false, // ⏩ a press that rests plays at 2× until it lifts (the reels)
}) {
  const vref = useRef(null);
  const mref = useRef(null);
  const watchedRef = useRef(onWatched);
  watchedRef.current = onWatched;
  const postNow = useRef(post);
  postNow.current = post;
  /* 🟢 ONE SILENT RETRY BEFORE SAYING ANYTHING. A clip whose first request lost
     the race with a reload almost always plays on the second; a clip that fails
     twice has something wrong with it worth telling the reader about. */
  const triedRef = useRef(false);
  const [failed, setFailed] = useState(false);
  const track = post?.audioTrackUrl || null;

  /* 🟥 THE LISTENER'S LEVEL SCALES THE AUTHOR'S MIX, IT DOES NOT REPLACE IT.
     A post carries its own balance between camera audio and the track it was
     scored with; multiplying keeps that balance intact at any volume, where
     assigning would flatten every post to the same loudness. Hooks cannot be
     conditional, so an un-scoped player still reads the store — it just ignores
     it and plays at the author's levels. */
  const surface = useVolume(volumeScope || 'feed');
  const scale = volumeScope ? surface.scale : 1;
  const silent = scale <= 0.001;
  const silentRef = useRef(silent);
  silentRef.current = silent;

  const originalMuted = muted || post?.isOriginalMuted === true || silent;
  const musicMuted = post?.isAddedSoundMuted === true || silent;
  /* the start offset, readable from inside the stable sync callback */
  const startRef = useRef(Number(post?.audioStartMs) || 0);
  startRef.current = Number(post?.audioStartMs) || 0;

  /* ⏩ HOLD TO PLAY AT 2× — the phone's gesture. A press that rests REEL_HOLD_MS
     speeds the clip up; lifting, leaving or cancelling puts it back. The click
     that ends a hold is swallowed, so the release never also pauses the reel.
     The song stops for the hold (see `sync`) and picks up in step after it. */
  const fastRef = useRef(false);
  const holdTimer = useRef(0);
  const swallowClick = useRef(false);
  const [fast, setFast] = useState(false);

  /* ── keep the song where the video is ── */
  const sync = useCallback(() => {
    const v = vref.current;
    const m = mref.current;
    if (!v || !m) return;
    // readyState < 3 means it can't play through — treat it as buffering, which
    // is the app's `st.isBuffering` check
    // silenced: there is nothing to hear, so don't hold a second stream open
    // (and don't hammer play() on an element the browser is refusing anyway)
    // ⏩ a held 2× is not "running" for the song — it cannot follow a clock at twice the speed
    const running = !v.paused && !v.ended && v.readyState >= 3 && !silentRef.current && !fastRef.current;
    if (!running) {
      if (!m.paused) { try { m.pause(); } catch {} }
      return;
    }
    const dur = Number.isFinite(m.duration) && m.duration > 0 ? m.duration : 0;
    /* 🎵 the song starts where the author put it and loops over what is left */
    const want = musicPositionFor(v.currentTime * 1000, dur * 1000, startRef.current) / 1000;
    if (m.paused) {
      try { m.currentTime = want; } catch {}
      m.play().catch(() => {});      // no gesture yet — the video still plays
    } else if (Math.abs(m.currentTime - want) > 0.3) {
      try { m.currentTime = want; } catch {}
    }
  }, []);

  /* ══ 📶 ADAPTIVE PLAYBACK ══════════════════════════════════════════════
     🟥 THE LADDER EXISTED AND NOTHING PLAYED IT. Every clip is transcoded into
        240p/480p/720p/1080p on the way in, and this element streamed the raw
        upload instead — full resolution, no way to step down, on whatever
        connection the reader had. See services/adaptiveVideo for why
        `<video src="…m3u8">` is not the fix.

     🟢 TORN DOWN ON EVERY CHANGE. An hls.js instance holds a MediaSource and
        several buffers; a reel feed that leaks one per swipe runs a phone out
        of memory in about two minutes. */
  /* 🎞 the ladder for THIS file — a later clip of a multi-video post has its own
     (media[i].hlsUrl), and the post's ladder is only ever the first clip's */
  const ladderUrl = hlsUrlFor(post, src);
  useEffect(() => attachAdaptive(vref.current, post, src), [ladderUrl, src]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* ══ ⏱ HOW MUCH OF IT WAS WATCHED ══════════════════════════════════════
     🟢 THE PLAYER IS THE ONLY THING THAT KNOWS. A parent can see that a card is
        on screen; only the element knows the playhead, and therefore whether
        somebody watched a clip through or left four hundred milliseconds in —
        which is the difference between the two strongest signals in the feed. */
  useEffect(() => {
    const v = vref.current;
    const id = post?.id;
    if (!v || !id) return undefined;
    const onPlay = () => feedSignals.watch.start(id);
    let watched = false;
    const onTime = () => {
      feedSignals.watch.progress(id, v.currentTime, v.duration);
      /* 👁️ a clip that has played a second and a half has been watched — once */
      if (!watched && v.currentTime >= 1.5 && watchedRef.current) { watched = true; try { watchedRef.current(postNow.current); } catch { /* the parent's */ } }
    };
    const onUnmute = () => { if (!v.muted) feedSignals.unmuted(id); };
    v.addEventListener('play', onPlay);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('volumechange', onUnmute);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('volumechange', onUnmute);
      /* 🟥 THE UNMOUNT IS THE END OF THE WATCH, and it is the only place that
         knows it ended. A record left open is a watch that never happened. */
      feedSignals.watch.end(id);
    };
  }, [post?.id]);

  /* volumes are properties, not attributes — React can't set them in JSX */
  useEffect(() => {
    /* 🎵 one arithmetic for the mix, shared with the phone: the author's levels,
       scaled by the listener's. The muted flags stay attributes, above. */
    const ev = effectiveVolumes({ originalVolume: post?.originalVolume, addedSoundVolume: post?.addedSoundVolume }, scale);
    const v = vref.current;
    if (v) v.volume = ev.original;
    const m = mref.current;
    if (m) m.volume = ev.added;
    // coming back from silence, pick the song up where the video already is
    // rather than waiting for the next timeupdate
    sync();
  }, [post?.originalVolume, post?.addedSoundVolume, post?.audioStartMs, scale, sync]);

  /* ✂️ 🟥 A TRIM IS A RANGE, NOT A SHORTER FILE. The browser cannot re-encode a
     clip without shipping a video encoder to every visitor, so a cut made here
     is stored as `trimStartMs`/`trimEndMs` and honoured at playback — by this
     element and by the phone's player, which is why the rule lives in a shared
     module rather than here.
     🟥 `ended` NEVER FIRES ON A TRIMMED CLIP, because playback is looped back
     before it can reach the real end. The loop is what `seekForTrim` decides;
     the lead-out inside it is what stops the last frames flashing past. */
  const trim = trimOf(post);
  useEffect(() => {
    const v = vref.current;
    if (!v || !trim) return undefined;
    const clamp = () => {
      const to = seekForTrim(v.currentTime * 1000, trim);
      if (to !== null) { try { v.currentTime = to / 1000; } catch {} }
    };
    clamp();
    v.addEventListener('timeupdate', clamp);
    v.addEventListener('loadedmetadata', clamp);
    return () => {
      v.removeEventListener('timeupdate', clamp);
      v.removeEventListener('loadedmetadata', clamp);
    };
  }, [trim?.start, trim?.end]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const v = vref.current;
    if (!v) return undefined;
    const events = ['play', 'playing', 'pause', 'ended', 'seeked', 'waiting', 'stalled', 'timeupdate'];
    events.forEach((e) => v.addEventListener(e, sync));
    return () => {
      events.forEach((e) => v.removeEventListener(e, sync));
      const m = mref.current;
      if (m) { try { m.pause(); } catch {} }
    };
  }, [sync, track]);

  /* ══ BARS ABOVE AND BELOW, OR A LITTLE OFF THE SIDES ═══════════════════════
     `contain` never crops, which is right for a photo and wrong for a reel: a
     clip even slightly narrower than the space it is in gets black bands top and
     bottom, and a reel with letterboxing looks like a mistake.

     🟥 THE CROP IS ONE-DIRECTIONAL, and that is the whole point. If the box is
        NARROWER than the video, filling it means shaving the sides — heads stay
        in frame, and the bands go. If the box is WIDER, filling it would eat the
        top and bottom of the picture, which is the very thing we are trying to
        prevent, so it stays contained and the spare room is simply black gutter.

     `fitTolerance` is how much side is allowed to go: past it (a landscape clip
     in a portrait pane) cropping would leave a sliver, so bars are the honest
     answer. */
  useEffect(() => {
    const v = vref.current;
    if (!v || !fitTolerance) return undefined;
    const decide = () => {
      const vw = v.videoWidth;
      const vh = v.videoHeight;
      if (!vw || !vh) return;
      const r = v.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const a = vw / vh;            // the picture
      const b = r.width / r.height; // the hole it has to go in
      const fill = b < a && b / a >= 1 - fitTolerance;
      v.style.objectFit = fill ? 'cover' : 'contain';
    };
    v.addEventListener('loadedmetadata', decide);
    v.addEventListener('loadeddata', decide);
    decide();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(decide); ro.observe(v); }
    return () => {
      v.removeEventListener('loadedmetadata', decide);
      v.removeEventListener('loadeddata', decide);
      if (ro) ro.disconnect();
    };
  }, [fitTolerance, src]);

  /* register with the arbiter: whoever plays last silences whoever played before */
  useEffect(() => {
    const v = vref.current;
    if (!v) return undefined;
    const stop = () => {
      try { v.pause(); } catch {}
      const m = mref.current;
      if (m) { try { m.pause(); } catch {} }
    };
    PLAYERS.set(v, stop);
    const onPlay = () => claimPlayback(v);
    v.addEventListener('play', onPlay);
    return () => {
      v.removeEventListener('play', onPlay);
      PLAYERS.delete(v);
      if (current === v) current = null;
    };
  }, []);

  /* play when it scrolls into view, pause when it leaves — the reel behaviour
     🟥 EXCEPT WHEN IT WAS PAUSED ON PURPOSE. Autoplay is a guess about what
        someone wants; a tap on the pause button is not a guess, and scrolling
        the video half out of the window and back must not overrule it. */
  const userPaused = useRef(false);
  useEffect(() => {
    const v = vref.current;
    if (!v || !autoPlayInView || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          if (!userPaused.current) v.play().catch(() => {});
        } else { v.pause(); const m = mref.current; if (m) { try { m.pause(); } catch {} } }
      },
      { threshold: [0, 0.6] },
    );
    io.observe(v);
    return () => io.disconnect();
  }, [autoPlayInView]);

  /* ── what the chrome needs to know: playing?, where?, how long? ──
     Only wired up when there IS chrome — a reel has no progress bar and should
     not re-render to feed one.

     🟥 THE LINE IS PAINTED, NOT RENDERED. `timeupdate` fires about four times a
        second, so a bar driven by it advances in visible steps — and re-renders
        the whole post four times a second to do it. The fill, the handle and
        the clock are written straight to their nodes from a requestAnimationFrame
        loop instead: sixty smooth frames, zero React work, and the loop only
        runs while something is actually playing.

     React state is left holding the two things that genuinely change the
     markup: is it playing (which glyph), and how long is it. */
  const [ui, setUi] = useState({ playing: false, d: 0 });
  const fillRef = useRef(null);
  const knobRef = useRef(null);
  const timeRef = useRef(null);
  const rafRef = useRef(0);

  const paint = useCallback(() => {
    const v = vref.current;
    if (!v) return;
    const d = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : 0;
    const t = v.currentTime || 0;
    const pc = d > 0 ? Math.max(0, Math.min(100, (t / d) * 100)) : 0;
    if (fillRef.current) fillRef.current.style.width = `${pc}%`;
    if (knobRef.current) knobRef.current.style.left = `${pc}%`;
    if (timeRef.current) {
      const next = fmt(t);
      // only touch the DOM when the second actually turns over
      if (timeRef.current.textContent !== next) timeRef.current.textContent = next;
    }
  }, []);

  useEffect(() => {
    const v = vref.current;
    if (!v || !chrome) return undefined;

    const loop = () => {
      paint();
      rafRef.current = requestAnimationFrame(loop);
    };
    const start = () => {
      if (!rafRef.current) rafRef.current = requestAnimationFrame(loop);
    };
    const stopLoop = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };

    const read = () => {
      setUi((prev) => {
        const playing = !v.paused && !v.ended;
        const d = Number.isFinite(v.duration) ? v.duration : 0;
        return prev.playing === playing && prev.d === d ? prev : { playing, d };
      });
      if (!v.paused && !v.ended) start(); else { stopLoop(); paint(); }
    };

    const events = ['play', 'playing', 'pause', 'ended', 'loadedmetadata', 'durationchange', 'seeked', 'emptied'];
    events.forEach((e) => v.addEventListener(e, read));
    // a scrub while paused moves nothing else — repaint on the seek itself
    v.addEventListener('seeking', paint);
    read();
    paint();
    return () => {
      events.forEach((e) => v.removeEventListener(e, read));
      v.removeEventListener('seeking', paint);
      stopLoop();
    };
  }, [chrome, src, paint]);

  const endHold = useCallback(() => {
    clearTimeout(holdTimer.current);
    holdTimer.current = 0;
    if (!fastRef.current) return;
    fastRef.current = false;
    setFast(false);
    const v = vref.current;
    if (v) v.playbackRate = 1;
    sync();
  }, [sync]);
  const startHold = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const v = vref.current;
    if (!v || v.paused) return;
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => {
      holdTimer.current = 0;
      const vv = vref.current;
      if (!vv || vv.paused) return;
      fastRef.current = true;
      swallowClick.current = true;
      setFast(true);
      vv.playbackRate = REEL_FAST_RATE;
      sync();
    }, REEL_HOLD_MS);
  };
  useEffect(() => () => clearTimeout(holdTimer.current), []);

  const toggle = (e) => {
    e?.stopPropagation?.();
    const v = vref.current;
    if (!v) return;
    if (v.paused || v.ended) { userPaused.current = false; v.play().catch(() => {}); }
    else { userPaused.current = true; v.pause(); const m = mref.current; if (m) { try { m.pause(); } catch {} } }
  };

  /* dragging the line scrubs — a progress bar you cannot move is a progress bar
     that lies about being a control */
  const barRef = useRef(null);
  const seekTo = (clientX) => {
    const v = vref.current;
    const bar = barRef.current;
    if (!v || !bar || !Number.isFinite(v.duration) || v.duration <= 0) return;
    const r = bar.getBoundingClientRect();
    if (r.width <= 0) return;
    const frac = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    try { v.currentTime = frac * v.duration; } catch {}
    paint();   // the handle follows the pointer in the same frame, not a seek later
  };
  const startSeek = (e) => {
    e.stopPropagation();
    e.preventDefault();
    seekTo(e.clientX);
    const move = (ev) => seekTo(ev.clientX);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <>
      <video
        ref={vref}
        className={className}
        /* 🟥 NO `src` WHEN THERE IS A LADDER. React would set the attribute on
           every render and yank the element away from hls.js mid-playback; the
           effect above owns the source in that case. */
        src={ladderUrl ? undefined : src}
        poster={poster || undefined}
        loop={loop}
        playsInline
        muted={originalMuted}
        controls={controls}
        preload="metadata"
        onClick={(e) => {
          if (swallowClick.current) { swallowClick.current = false; e.preventDefault(); e.stopPropagation(); return; }
          if (onClick) onClick(e);
        }}
        onPointerDown={holdToFast ? startHold : undefined}
        onPointerUp={holdToFast ? endHold : undefined}
        onPointerLeave={holdToFast ? endHold : undefined}
        onPointerCancel={holdToFast ? endHold : undefined}
        onContextMenu={holdToFast ? (e) => { if (fastRef.current) e.preventDefault(); } : undefined}
        /* 🟥 A FEED VIDEO THAT FAILED WAS A BLACK RECTANGLE, FOR EVER. There was
           no `onError` here at all — no message, no retry, nothing to tell the
           reader whether the clip was broken or still loading. A picture at
           least renders the browser's torn-paper glyph; a <video> renders
           nothing, which is the least informative failure in the product.

           🟢 ONE RELOAD, THEN A SENTENCE. `load()` re-requests the same source,
              which is exactly what a transient failure needs and costs a
              permanent one one round trip. */
        onError={() => {
          if (triedRef.current) { setFailed(true); return; }
          triedRef.current = true;
          try { vref.current?.load(); } catch { setFailed(true); }
        }}
      />
      {fast && (
        <div className="pv-fast" aria-live="polite">{REEL_FAST_RATE}× <span aria-hidden="true">▸▸</span></div>
      )}
      {failed && (
        <div className="pv-dead">
          <span>This video could not be loaded</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              triedRef.current = false;
              setFailed(false);
              try { vref.current?.load(); } catch { setFailed(true); }
            }}
          >
            Try again
          </button>
        </div>
      )}
      {track && !gestureless && (
        /* no `autoPlay`, no `controls`: this element is driven entirely by the
           video's clock above. Hidden rather than display:none — a display:none
           media element is allowed to be dropped by some browsers. */
        <audio
          ref={mref}
          src={track}
          loop
          preload="auto"
          muted={musicMuted}
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        />
      )}

      {/* ══ THE FEED'S CHROME ═════════════════════════════════════════════
          A feed video plays on its own, so the two questions it has to answer
          without being asked are "how do I stop this" and "how long is it".
          Pause, elapsed / total, and a line you can drag — pinned to the
          bottom edge, on top of the picture, and every one of them swallows
          its own clicks so using them never counts as opening the post. */}
      {chrome && (
        <div className="vb-vchrome" onClick={(e) => e.stopPropagation()}>
          <button
            className="vb-vplay" onClick={toggle}
            aria-label={ui.playing ? 'Pause' : 'Play'} title={ui.playing ? 'Pause' : 'Play'}
          >
            {ui.playing ? <Pause width={15} height={15} /> : <Play width={15} height={15} />}
          </button>
          {/* the elapsed half is written by the rAF loop, so it is a bare node
              React never re-renders; the total only changes when the file does */}
          <span className="vb-vtime"><b ref={timeRef}>0:00</b> <i>/</i> {fmt(ui.d)}</span>
          <div
            ref={barRef} className="vb-vbar"
            role="slider" aria-label="Seek"
            aria-valuemin={0} aria-valuemax={Math.round(ui.d)}
            tabIndex={0}
            onPointerDown={startSeek}
            onKeyDown={(e) => {
              const v = vref.current;
              if (!v || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
              e.preventDefault(); e.stopPropagation();
              try { v.currentTime = Math.max(0, v.currentTime + (e.key === 'ArrowRight' ? 5 : -5)); } catch {}
            }}
          >
            <span ref={fillRef} className="vb-vbar-fill" style={{ width: '0%' }} />
            <span ref={knobRef} className="vb-vbar-knob" style={{ left: '0%' }} />
          </div>
          {volumeScope && <VolumeControl scope={volumeScope} place="left" className="vb-vol-inline" />}
        </div>
      )}
    </>
  );
}
